import { UserIncentive } from '@tovala/browser-apis-combinedapi'
import { events } from 'analytics/events'
import Link from 'components/common/Link'
import { useVariantByScreenSize } from 'hooks/variantByScreenSize'
import { ComponentProps } from 'react'
import { UserIncentiveWithFreeDessert } from 'types/internal'
import { track } from 'utils/analytics'
import { DATE_FORMATS, formatDate } from 'utils/dates'
import {
  getAppliesToOrdersText,
  getIncentivesToDisplay,
  getMealDiscountText,
  getRemainingOrdersCount,
  hasFreeShipping,
  hasMealDiscount,
  isLegacyFreeDessertIncentive,
} from 'utils/incentives'

type LinkStyle = ComponentProps<typeof Link>['linkStyle']
type LinkSize = ComponentProps<typeof Link>['size']

const AccountIncentives = ({
  activeIncentives,
  isFreeDessertCustomer,
}: {
  activeIncentives: UserIncentive[]
  isFreeDessertCustomer: boolean
}) => {
  const incentivesToDisplay = getIncentivesToDisplay({
    activeIncentives,
    isFreeDessertCustomer,
  })

  return (
    <div className="h-full">
      <h2 className="text-k/28_130 md:text-k/20_110">Promotions</h2>
      {incentivesToDisplay.length > 0 ? (
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-1">
          {incentivesToDisplay.map((incentive) => {
            const content = getIncentiveCardContent({ incentive })
            if (!content) {
              return null
            }
            return (
              <IncentiveCard
                key={incentive.offerID}
                expirationText={getExpirationText(incentive)}
                offerID={incentive.offerID}
                subtitle={content.subtitle}
                title={content.title}
              />
            )
          })}
        </div>
      ) : (
        <div className="flex h-full flex-col items-center justify-center space-y-4 text-center">
          <h3 className="text-k/20_110">No active promos</h3>
          <p className="text-k/14_120 text-grey-10">
            Future Tovala promotions will show up here
          </p>
        </div>
      )}
    </div>
  )
}

export default AccountIncentives

const IncentiveCard = ({
  expirationText,
  offerID,
  title,
  subtitle,
}: {
  expirationText: string
  offerID: string
  title: string
  subtitle: string
}) => {
  const [linkStyle, linkSize] = useVariantByScreenSize<[LinkStyle, LinkSize]>(
    ['stroke', 'medium'],
    {
      md: ['orangeText', 'fluid'],
    },
  )
  return (
    <div className="flex flex-col justify-between space-y-4 rounded-lg bg-grey-2 p-4">
      <div className="space-y-4">
        <h3 className="text-k/20_110">{title}</h3>
        <div className="space-y-1 text-k/14_120">
          <p>{expirationText}</p>
          <p className="text-grey-9">{subtitle}</p>
        </div>
      </div>

      <div className="max-w-[137px] md:max-w-fit">
        <Link
          linkStyle={linkStyle}
          onClick={() => {
            track(events.PROMO_LEARN_MORE, {
              offer_id: offerID,
            })
          }}
          size={linkSize}
          to={`/terms/offers/${offerID}`}
        >
          Learn More
        </Link>
      </div>
    </div>
  )
}

function getIncentiveCardContent({
  incentive,
}: {
  incentive: UserIncentiveWithFreeDessert
}) {
  const { titleOverride } = incentive
  const isUnlimited = incentive.totalOrdersCount === null
  const appliesToOrders = getAppliesToOrdersText(incentive)
  const appliedToOrders = getAppliedToOrdersText(incentive)

  let title = ''
  let subtitle = ''

  if (hasFreeShipping(incentive) && hasMealDiscount(incentive)) {
    title = `${getMealDiscountText(incentive)} and free shipping on ${appliesToOrders}`
    subtitle = isUnlimited
      ? `Free shipping and ${getMealDiscountText(incentive)} applied to each week's order.`
      : `Free shipping and promo off will be applied to ${appliedToOrders}. Canceling will remove this promotion.`
  } else if (hasFreeShipping(incentive)) {
    title = isUnlimited
      ? 'Free shipping for life'
      : `Free shipping on ${appliesToOrders}`
    subtitle = isUnlimited
      ? "Free shipping applied to each week's order."
      : `Free shipping will be applied to ${appliedToOrders}. Canceling will remove this promotion.`
  } else if (hasMealDiscount(incentive)) {
    title = `${getMealDiscountText(incentive)} ${appliesToOrders}`
    subtitle = isUnlimited
      ? `${getMealDiscountText(incentive)} applied to each week's order.`
      : `Promo off will be applied to ${appliedToOrders}. This includes all costs aside from shipping. Canceling will remove this promotion.`
  } else if (isLegacyFreeDessertIncentive(incentive)) {
    title = 'Free dessert for life'
    subtitle =
      'Add a dessert and get it free. Discount applied when order closes. Canceling will remove this promotion.'
  }

  return {
    title: titleOverride || title,
    subtitle,
  }
}

function getExpirationText(incentive: UserIncentiveWithFreeDessert): string {
  if (incentive.useByDate === null) {
    return 'Never expires'
  }

  return `Use by ${formatDate(incentive.useByDate, {
    format: DATE_FORMATS.MONTH_FULL_DAY_YEAR_FULL,
  })}`
}

function getAppliedToOrdersText(incentive: UserIncentiveWithFreeDessert) {
  const { redeemedOrderCount, totalOrdersCount } = incentive

  const usedCount =
    redeemedOrderCount > 0
      ? ` (${redeemedOrderCount}/${totalOrdersCount} used)`
      : ''

  const appliesToOrders =
    getRemainingOrdersCount(incentive) === 1
      ? 'your next processed order'
      : getAppliesToOrdersText(incentive)

  return `${appliesToOrders}${usedCount}`
}
